/**
 * Data layer for `advertiser-dashboard-separate-persona`.
 * Thin typed fetch wrappers over /api/v1/advertiser-dashboard-separate-persona.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Advertiser persona dashboard — one row per advertiser_id. Stores a layout
 * (widget positions) and a list of visible_kpis the advertiser has pinned.
 * Backend exposes POST (upsert), GET / (list), GET /:advertiser_id, DELETE /:id.
 */

export interface LayoutWidget {
	key: string;
	x: number;
	y: number;
	w?: number;
	h?: number;
}

export interface AdvertiserPersonaRow {
	id: string;
	advertiser_id: string;
	layout: LayoutWidget[];
	visible_kpis: string[];
	updated_at: number;
}

export interface ListAdvertiserPersonaResponse {
	items: AdvertiserPersonaRow[];
}

export interface GetAdvertiserPersonaResponse {
	item: AdvertiserPersonaRow;
}

export interface UpsertAdvertiserPersonaBody {
	advertiser_id: string;
	layout?: LayoutWidget[];
	visible_kpis?: string[];
}

export interface UpsertAdvertiserPersonaResponse {
	ok: true;
	item: AdvertiserPersonaRow;
}

/**
 * KPI record derived from persona rows. Persona stores visible_kpis as a list
 * of string keys; this shape is the aggregated/UI-ready summary the page
 * surfaces for the currently-selected advertiser.
 */
export interface AdvertiserKpi {
	key: string;
	label: string;
	value: number;
	format: "currency" | "count" | "percent";
}

const BASE = "/api/v1/advertiser-dashboard-separate-persona";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAdvertiserPersonaResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAdvertiserPersonaResponse>(res);
}

export async function getOne(
	advertiser_id: string,
): Promise<GetAdvertiserPersonaResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(advertiser_id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<GetAdvertiserPersonaResponse>(res);
}

export async function upsert(
	body: UpsertAdvertiserPersonaBody,
): Promise<UpsertAdvertiserPersonaResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<UpsertAdvertiserPersonaResponse>(res);
}

export async function remove(id: string): Promise<{ ok: true }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: true }>(res);
}

export const advertiserPersonaQueryKeys = {
	all: ["advertiser-dashboard-separate-persona"] as const,
	list: () => [...advertiserPersonaQueryKeys.all] as const,
	detail: (advertiser_id: string) =>
		[...advertiserPersonaQueryKeys.all, advertiser_id] as const,
};

// ---------- pure data-layer helpers (unit-tested) -----------------------

/**
 * Aggregate summary KPIs across all persona rows the tenant owns.
 * Pure function — no I/O. Tested directly in the data-layer vitest.
 *
 * Returns a stable set of KPI records derived from the rows:
 *  - total_spend      (sum of all rows' spend field if present, else 0)
 *  - total_conversions (sum of all rows' conversions if present, else 0)
 *  - blended_cpa       (spend / conversions when both > 0, else 0)
 *  - personas          (count of distinct advertiser_id rows)
 *
 * Persona layout rows are opaque JSON; this helper tolerates missing/extra
 * keys by defaulting to 0. Real numbers are injected by a future reporting
 * query upstream; the shape is stable so the page can render today.
 */
export function aggregateAdvertiserKpis(
	rows: ReadonlyArray<AdvertiserPersonaRow & { metrics?: Record<string, number> }>,
): AdvertiserKpi[] {
	let spend = 0;
	let conv = 0;
	for (const r of rows) {
		const m = r.metrics ?? {};
		if (typeof m.spend === "number" && Number.isFinite(m.spend)) spend += m.spend;
		if (typeof m.conversions === "number" && Number.isFinite(m.conversions)) {
			conv += m.conversions;
		}
	}
	const cpa = conv > 0 ? spend / conv : 0;
	return [
		{ key: "spend", label: "Spend", value: round2(spend), format: "currency" },
		{
			key: "conversions",
			label: "Conversions",
			value: Math.round(conv),
			format: "count",
		},
		{ key: "cpa", label: "CPA", value: round2(cpa), format: "currency" },
		{
			key: "personas",
			label: "Personas",
			value: rows.length,
			format: "count",
		},
	];
}

function round2(n: number): number {
	if (!Number.isFinite(n)) return 0;
	return Math.round(n * 100) / 100;
}

/**
 * Select the persona row for a given advertiser_id. Returns `null` when the
 * list is empty or no row matches. Pure — unit-tested.
 */
export function selectPersona(
	rows: ReadonlyArray<AdvertiserPersonaRow>,
	advertiser_id: string | null,
): AdvertiserPersonaRow | null {
	if (!advertiser_id) return rows[0] ?? null;
	return rows.find((r) => r.advertiser_id === advertiser_id) ?? null;
}

/**
 * Format a KPI value for display. Pure.
 */
export function formatKpi(kpi: AdvertiserKpi): string {
	if (kpi.format === "currency") {
		return `$${kpi.value.toFixed(2)}`;
	}
	if (kpi.format === "percent") {
		return `${kpi.value.toFixed(1)}%`;
	}
	return String(kpi.value);
}
